import { 
  User, 
  SearchFormData, 
  SearchRequest, 
  SearchParameterType, 
  PaginatedResponse,
  UniversalOSINTSearchData,
  UniversalOSINTResponse,
  UniversalOSINTResults,
  UniversalOSINTRequestSummary,
  UniversalOSINTRequest,
  LawEnforcementProfile,
  LawEnforcementProfileFormData,
  GovernmentEmailOTPData,
  GovernmentEmailVerifyData,
  ReportTemplate,
  AIProvider,
  AIProviderFormData,
  AISummaryConfig
} from '../types';
import {
  APIKey,
  APIKeyCreate,
  APIKeyUpdate,
  APIKeyUsage,
  APIKeyStats,
  APIKeyTest,
  APIKeyTestResult,
  APIKeyDocumentation
} from '../types/apiKeys';

// Utility function to convert camelCase to snake_case
function camelToSnakeCase(obj: any): any {
  if (obj === null || obj === undefined) {
    return obj;
  }
  
  if (Array.isArray(obj)) {
    return obj.map(camelToSnakeCase);
  }
  
  if (typeof obj === 'object') {
    const snakeCaseObj: any = {};
    for (const [key, value] of Object.entries(obj)) {
      // Special handling for HTTP headers and request_parameters - don't convert them
      if ((key === 'headers' || key === 'request_parameters') && typeof value === 'object') {
        snakeCaseObj[key] = value; // Preserve headers and request_parameters as-is
      } else {
        const snakeKey = key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);
        snakeCaseObj[snakeKey] = camelToSnakeCase(value);
      }
    }
    return snakeCaseObj;
  }
  
  return obj;
}

// Centralized API Error Types
export class APIError extends Error {
  constructor(
    message: string,
    public status: number,
    public code?: string,
    public details?: any
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export class AuthenticationError extends APIError {
  constructor(message = 'Authentication failed') {
    super(message, 401, 'AUTH_ERROR');
  }
}

export class AuthorizationError extends APIError {
  constructor(message = 'Access denied') {
    super(message, 403, 'FORBIDDEN');
  }
}

export class ValidationError extends APIError {
  constructor(message = 'Validation failed', details?: any) {
    super(message, 400, 'VALIDATION_ERROR', details);
  }
}

export class NetworkError extends APIError {
  constructor(message = 'Network error') {
    super(message, 0, 'NETWORK_ERROR');
  }
}

// Request/Response Interceptors
interface RequestInterceptor {
  (config: RequestInit): RequestInit;
}

interface ResponseInterceptor {
  (response: Response, data: any): any;
}

interface ErrorInterceptor {
  (error: APIError): void;
}

interface ExtendedRequestInit extends RequestInit {
  timeoutMs?: number;
}

// Centralized API Service
class CentralizedAPIService {
  private baseURL: string;
  private token: string | null;
  private requestInterceptors: RequestInterceptor[] = [];
  private responseInterceptors: ResponseInterceptor[] = [];
  private errorInterceptors: ErrorInterceptor[] = [];
  private pendingRequests: Map<string, Promise<any>> = new Map();

  constructor() {
    // Use relative URL in development to avoid CORS via proxy (package.json "proxy": "http://localhost:9000")
    const useProxy = process.env.NODE_ENV === 'development' && process.env.REACT_APP_USE_PROXY !== 'false';
    this.baseURL = useProxy ? '/backend/api/v1' : (process.env.REACT_APP_API_URL || 'http://localhost:9000/backend/api/v1');
    this.token = localStorage.getItem('authToken');
    
    // Add default interceptors
    this.addRequestInterceptor(this.addAuthHeader.bind(this));
    this.addResponseInterceptor(this.normalizeResponse.bind(this));
    this.addErrorInterceptor(this.handleError.bind(this));
  }

  // Interceptor Management
  addRequestInterceptor(interceptor: RequestInterceptor) {
    this.requestInterceptors.push(interceptor);
  }

  addResponseInterceptor(interceptor: ResponseInterceptor) {
    this.responseInterceptors.push(interceptor);
  }

  addErrorInterceptor(interceptor: ErrorInterceptor) {
    this.errorInterceptors.push(interceptor);
  }

  // Default Interceptors
  private addAuthHeader(config: RequestInit): RequestInit {
    if (this.token) {
      console.log('Adding auth header with token:', this.token.substring(0, 10) + '...');
      return {
        ...config,
        headers: {
          ...config.headers,
          'Authorization': `Token ${this.token}`,
        },
      };
    } else {
      console.warn('No auth token found in localStorage');
    }
    return config;
  }

  private normalizeResponse(response: Response, data: any): any {
    // Don't normalize paginated responses - they need to keep their structure
    // The normalizeResponse was causing issues by returning only results
    // Return data as-is and let individual methods handle pagination
    return data;
  }

  private handleError(error: APIError): void {
    console.error('API Error:', error);
    
    // Auto-logout on authentication errors
    if (error instanceof AuthenticationError) {
      console.warn('Authentication error detected, logging out user');
      this.logout();
    }
  }

  // Request Deduplication
  private getRequestKey(endpoint: string, options: RequestInit): string {
    const method = options.method || 'GET';
    const body = options.body ? JSON.stringify(options.body) : '';
    return `${method}:${endpoint}:${body}`;
  }

  // Main Request Method
  public async request<T>(
    endpoint: string,
    options: ExtendedRequestInit = {}
  ): Promise<T> {
    // Refresh token from localStorage before making request
    this.token = localStorage.getItem('authToken');
    
    const url = `${this.baseURL}${endpoint}`;
    let config: ExtendedRequestInit = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    // Apply request interceptors
    for (const interceptor of this.requestInterceptors) {
      config = interceptor(config);
    }

    const requestKey = this.getRequestKey(endpoint, config);
    
    // Check for pending request
    if (this.pendingRequests.has(requestKey)) {
      console.log('Request deduplication: returning pending request for', endpoint);
      return this.pendingRequests.get(requestKey)!;
    }

    console.log('API Request:', {
      url,
      method: config.method || 'GET',
      hasAuthHeader: !!(config.headers as Record<string, string>)?.Authorization
    });

    const requestPromise = this.executeRequest<T>(url, config);
    this.pendingRequests.set(requestKey, requestPromise);

    try {
      const result = await requestPromise;
      return result;
    } finally {
      this.pendingRequests.delete(requestKey);
    }
  }

  private async executeRequest<T>(url: string, config: ExtendedRequestInit): Promise<T> {
    // Add timeout to fetch request (defaults to 60 seconds)
    const controller = new AbortController();
    const timeoutMs = typeof config.timeoutMs === 'number' && config.timeoutMs > 0 ? config.timeoutMs : 60000;
    const timeoutId = setTimeout(() => {
      console.warn(`Request timeout after ${Math.round(timeoutMs / 1000)} seconds:`, url);
      controller.abort();
    }, timeoutMs);
    
    try {
      console.log('Executing fetch request:', { 
        url, 
        method: config.method || 'GET', 
        headers: config.headers,
        hasSignal: !!config.signal
      });
      
      // Ensure signal is properly set (use our controller's signal if config doesn't have one)
      const fetchConfig: RequestInit = {
        ...config,
        signal: config.signal || controller.signal
      };
      delete (fetchConfig as any).timeoutMs;
      
      console.log('About to call fetch with config:', {
        url,
        method: fetchConfig.method,
        hasSignal: !!fetchConfig.signal,
        signalAborted: fetchConfig.signal?.aborted
      });
      
      const response = await fetch(url, fetchConfig);
      
      clearTimeout(timeoutId);
      
      // Check if response has content before trying to parse JSON
      const contentType = response.headers.get('content-type');
      let data: any = null;
      
      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (jsonError) {
          // If JSON parsing fails, check if it's an empty response
          if (response.status === 204 || response.status === 200) {
            data = null; // Empty response is OK for DELETE requests
          } else {
            throw jsonError;
          }
        }
      } else if (response.status === 204) {
        // No content response (common for DELETE requests)
        data = null;
      }

      console.log('API Response:', {
        status: response.status,
        ok: response.ok,
        data,
        contentType
      });

      if (!response.ok) {
        throw this.createAPIError(response, data);
      }

      // Apply response interceptors
      let processedData = data;
      for (const interceptor of this.responseInterceptors) {
        processedData = interceptor(response, processedData);
      }

      return processedData;
    } catch (error) {
      // Always clear timeout on error
      clearTimeout(timeoutId);
      
      console.error('Fetch error details:', {
        url,
        error: error instanceof Error ? error.message : String(error),
        errorType: error instanceof TypeError ? 'TypeError' : error instanceof Error ? error.constructor.name : 'Unknown',
        errorStack: error instanceof Error ? error.stack : undefined
      });
      
      if (error instanceof APIError) {
      throw error;
      }
      
      // Network or other errors - provide more specific error messages
      let errorMessage = 'Request failed';
      if (error instanceof TypeError) {
        if (error.message === 'Failed to fetch') {
          errorMessage = `Network error: Unable to connect to ${url}. Please check if the server is running and accessible.`;
        } else {
          errorMessage = `Network error: ${error.message}`;
        }
      } else if (error instanceof Error) {
        // Handle AbortError (timeout)
        if (error.name === 'AbortError') {
          errorMessage = `Request timeout: The server took too long to respond. Please try again.`;
        } else {
          errorMessage = error.message;
        }
      }
      
      const apiError = new NetworkError(errorMessage);
      
      // Apply error interceptors
      for (const interceptor of this.errorInterceptors) {
        interceptor(apiError);
      }
      
      throw apiError;
    }
  }

  private createAPIError(response: Response, data: any): APIError {
    const status = response.status;
    const detailMsg = Array.isArray(data?.detail) ? data.detail[0] : data?.detail;
    const message = data?.error || data?.message || detailMsg || (status === 404 ? 'Resource not found' : status === 500 ? 'Internal server error' : `HTTP ${status}`);
    const details = data?.errors ?? data;
    switch (status) {
      case 400:
        return new ValidationError(message, details);
      case 401:
        return new AuthenticationError(message);
      case 403:
        return new AuthorizationError(message);
      case 404:
        return new APIError(message, 404, 'NOT_FOUND', details);
      case 500:
        return new APIError(message, 500, 'SERVER_ERROR', details);
      default:
        return new APIError(message, status, undefined, details);
    }
  }

  // Public method for admin API requests
  public async adminRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    // Refresh token from localStorage before making admin request
    this.token = localStorage.getItem('authToken');
    
    // Add cache-busting for GET requests to ensure fresh data
    if (!options.method || options.method === 'GET') {
      const separator = endpoint.includes('?') ? '&' : '?';
      const timestamp = Date.now();
      endpoint = `${endpoint}${separator}_t=${timestamp}`;
      
      // Add cache-busting headers
      options.headers = {
        ...options.headers,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      };
    }
    
    const response = await this.request<T>(endpoint, options);
    return response;
  }

  // Authentication Methods
  async getGoogleOAuthUrl(): Promise<{ auth_url: string }> {
    const response = await this.request<{ auth_url: string }>('/auth/google/url/');
    return response;
  }

  async handleGoogleOAuthCallback(code: string): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/google/callback/', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async googleAuth(googleData: { email: string; name: string; given_name: string; family_name: string; picture: string }): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/google/', {
      method: 'POST',
      body: JSON.stringify(googleData),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async login(credentials: { email: string; password: string }): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async register(userData: { email: string; password: string; firstName: string; lastName: string }): Promise<{ user: User; token: string }> {
    const response = await this.request<{ user: User; token: string }>('/auth/register/', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
    
    if (response.token) {
      this.setToken(response.token);
    }
    
    return response;
  }

  async logout(): Promise<void> {
    try {
      // Call backend logout endpoint to invalidate token
      if (this.token) {
        await this.request('/auth/logout/', {
          method: 'POST',
        });
      }
    } catch (e) {
      // Ignore errors, always clear token locally
    }
    this.token = null;
    localStorage.removeItem('authToken');
    console.log('User logged out, token cleared');
  }

  // User Profile Methods
  async getUserProfile(): Promise<User> {
    const response = await this.request<User>('/user/profile/');
    return response;
  }

  async updateUserProfile(userData: Partial<User>): Promise<User> {
    const response = await this.request<User>('/user/profile/update/', {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
    return response;
  }


  // Password Management Methods
  async getPasswordStatus(): Promise<{
    isGoogleUser: boolean;
    passwordSet: boolean;
    canChangePassword: boolean;
    needsPasswordSet: boolean;
  }> {
    const response = await this.request<{
      isGoogleUser: boolean;
      passwordSet: boolean;
      canChangePassword: boolean;
      needsPasswordSet: boolean;
    }>('/user/password/status/');
    return response;
  }

  async changePassword(passwordData: {
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>('/user/password/change/', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
    return response;
  }

  async setPassword(passwordData: {
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ success: boolean; message: string }> {
    const response = await this.request<{ success: boolean; message: string }>('/user/password/set/', {
      method: 'POST',
      body: JSON.stringify(passwordData),
    });
    return response;
  }

  // Search Methods
  async getSearchHistory(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    parameter_type?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<SearchRequest>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, value.toString());
        }
      });
    }
    
    const queryString = query.toString();
    const endpoint = queryString ? `/search/?${queryString}` : '/search/';
    const response = await this.request<any>(endpoint);
    
    // Handle both paginated and non-paginated responses
    if (response.results !== undefined) {
      // Paginated response
      return response as PaginatedResponse<SearchRequest>;
    } else if (Array.isArray(response)) {
      // Non-paginated array response
      return {
        count: response.length,
        next: undefined,
        previous: undefined,
        results: response
      } as PaginatedResponse<SearchRequest>;
    } else {
      // Fallback
      return {
        count: 0,
        next: undefined,
        previous: undefined,
        results: []
      } as PaginatedResponse<SearchRequest>;
    }
  }

  async createSearch(searchData: SearchFormData): Promise<SearchRequest> {
    try {
      // Convert SearchFormData to backend format
      const requestData: any = {
        parameter_type_id: parseInt(searchData.parameterType),
        search_value: searchData.searchValue,
      };
      
      // Add country_code if provided
      if (searchData.countryCode) {
        requestData.country_code = searchData.countryCode;
      }
      
      const response = await this.request<SearchRequest>('/search/', {
        method: 'POST',
        body: JSON.stringify(requestData),
      });
      return response;
    } catch (error) {
      // Handle specific error types
      if (error instanceof APIError) {
        if (error.status === 400 && error.message.includes('Insufficient')) {
          throw new Error('Insufficient Int Coins for this search. Please add more credits to continue.');
        } else if (error.status === 400 && error.message.includes('Invalid')) {
          throw new Error('Invalid search parameters. Please check your input and try again.');
        } else if (error.status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        } else if (error.status === 403) {
          throw new Error('You do not have permission to perform this action.');
        }
      }
      throw error;
    }
  }

  async getSearchRequest(id: number): Promise<SearchRequest> {
    const response = await this.request<SearchRequest>(`/search/${id}/`);
    return response;
  }

  async getAPIRequests(searchId: number): Promise<any[]> {
    // Add cache-busting timestamp to ensure fresh data
    const timestamp = Date.now();
    const response = await this.request<any[]>(`/search/${searchId}/api-requests/?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
      },
    });
    return response;
  }

  async getParameterTypes(): Promise<SearchParameterType[]> {
    const response = await this.request<PaginatedResponse<SearchParameterType>>('/search-parameters/');
    return response.results || response;
  }

  // Admin - All Searches
  async adminGetAllSearches(params?: {
    page?: number;
    page_size?: number;
    status?: string;
    parameter_type?: number;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, value.toString());
        }
      });
    }
    const queryString = query.toString();
    const endpoint = queryString ? `/admin/searches/?${queryString}` : '/admin/searches/';
    const response = await this.request<any>(endpoint);
    if (response && Array.isArray(response)) {
      return {
        count: response.length,
        next: undefined,
        previous: undefined,
        results: response,
      } as PaginatedResponse<any>;
    }
    return response as PaginatedResponse<any>;
  }

  async adminRefundSearchIntCoins(searchId: number): Promise<{ success: boolean; message: string }> {
    return await this.request<{ success: boolean; message: string }>(`/admin/searches/${searchId}/refund/`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'Admin refund' })
    });
  }

  // Subscription Methods
  async getSubscriptionPlans(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/subscriptions/plans/');
    return response.results || response;
  }

  async getPublicSubscriptionPlans(): Promise<any[]> {
    const response = await this.request<any[]>('/subscriptions/public/plans/');
    return response;
  }

  async getUserSubscriptions(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/subscriptions/');
    return response.results || response;
  }

  // Payment Methods
  async createPayment(paymentData: any): Promise<any> {
    const response = await this.request<any>('/payments/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
    return response;
  }

  async getPaymentHistory(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/payments/');
    return response.results || response;
  }

  // Credits Methods
  async getIntCoinsTransactions(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/credits/transactions/');
    return response.results || response;
  }

  // OSINT Methods
  async getOSINTAPIs(): Promise<any[]> {
    return this.request('/osint/apis/');
  }

  async createOSINTRequest(requestData: { osintApi: number; searchValue: string }): Promise<any> {
    return this.request('/osint/requests/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(requestData)),
    });
  }

  async getOSINTRequests(): Promise<any[]> {
    return this.request('/osint/requests/');
  }

  // New methods for OSINT test page
  async getOsintApis(): Promise<any[]> {
    return this.request('/osint/apis/');
  }

  async createOsintRequest(data: {
    osint_api: string | number;
    search_value: string;
    request_data?: any;
  }): Promise<any> {
    console.log('Creating OSINT request with data:', data);
    const response = await this.request('/osint/requests/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(data)),
    });
    console.log('OSINT request response:', response);
    return response;
  }

  async getOsintRequestResults(id: number): Promise<any> {
    return this.request(`/osint/requests/${id}/results/`);
  }

  // Chatbot Methods
  async getChatbotSessions(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/chatbot/sessions/');
    return response.results || response;
  }

  async getChatbotMessages(sessionId: number): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>(`/chatbot/sessions/${sessionId}/messages/`);
    return response.results || response;
  }

  async sendChatbotMessage(sessionId: number, message: string): Promise<any> {
    const response = await this.request<any>(`/chatbot/sessions/${sessionId}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ message }),
    });
    return response;
  }

  // Document Upload
  async uploadDocument(formData: FormData): Promise<any> {
    const url = `${this.baseURL}/uploads/documents/`;
    const config: RequestInit = {
      method: 'POST',
      headers: {
        ...(this.token && { Authorization: `Token ${this.token}` }),
      },
      body: formData,
    };

    try {
      const response = await fetch(url, config);
      const data = await response.json();

      if (!response.ok) {
        throw this.createAPIError(response, data);
      }

      return data;
    } catch (error) {
      if (error instanceof APIError) {
      throw error;
      }
      throw new NetworkError('Document upload failed');
    }
  }

  // Search Parameter Types
  async getSearchParameters(): Promise<SearchParameterType[]> {
    const response = await this.request<PaginatedResponse<SearchParameterType>>('/search-parameters/');
    return response.results || response;
  }

  async getOrganizations(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/organizations/');
    return response.results || response;
  }

  async createOrganization(orgData: any): Promise<any> {
    const snakeCaseData = camelToSnakeCase(orgData);
    const response = await this.request<any>('/organizations/', {
      method: 'POST',
      body: JSON.stringify(snakeCaseData),
    });
    return response;
  }

  async updateOrganization(orgId: number, orgData: any): Promise<any> {
    const snakeCaseData = camelToSnakeCase(orgData);
    const response = await this.request<any>(`/organizations/${orgId}/`, {
      method: 'PUT',
      body: JSON.stringify(snakeCaseData),
    });
    return response;
  }

  async deleteOrganization(orgId: number): Promise<void> {
    await this.request<void>(`/organizations/${orgId}/`, {
      method: 'DELETE',
    });
  }

  async getSearchParameter(id: number): Promise<SearchParameterType> {
    return this.request<SearchParameterType>(`/search-parameters/${id}/`);
  }

  async createSearchParameter(data: any): Promise<SearchParameterType> {
    return this.request<SearchParameterType>('/search-parameters/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSearchParameter(id: number, data: Partial<any>): Promise<SearchParameterType> {
    return this.request<SearchParameterType>(`/search-parameters/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSearchParameter(id: number): Promise<void> {
    return this.request<void>(`/search-parameters/${id}/`, {
      method: 'DELETE',
    });
  }

  async activateSearchParameter(id: number): Promise<SearchParameterType> {
    return this.request<SearchParameterType>(`/search-parameters/${id}/activate/`, {
      method: 'POST',
    });
  }

  async deactivateSearchParameter(id: number): Promise<SearchParameterType> {
    return this.request<SearchParameterType>(`/search-parameters/${id}/deactivate/`, {
      method: 'POST',
    });
  }

  async testSearchParameterValidation(id: number, testData: any): Promise<any> {
    return this.request<any>(`/search-parameters/${id}/test_validation/`, {
      method: 'POST',
      body: JSON.stringify(testData),
    });
  }

  async getActiveSearchParameters(): Promise<SearchParameterType[]> {
    const response = await this.request<PaginatedResponse<SearchParameterType>>('/search-parameters/active_only/');
    return response.results || response;
  }

  async getSearchResults(searchId: number): Promise<any> {
    const response = await this.request<any>(`/search/${searchId}/results/`);
    return response;
  }

  async getSearchReport(searchId: number): Promise<any> {
    // Use request method for consistent error handling and interceptors
    // Add cache-busting timestamp to ensure fresh data
    const timestamp = Date.now();
    const endpoint = `/search/${searchId}/results/?_t=${timestamp}`;
    
    // Use request method which handles auth headers, errors, and interceptors properly
    // Don't override headers - let the request method handle them
    const response = await this.request<any>(endpoint, {
      timeoutMs: 180000, // report payload can be large; allow longer server processing time
    });
    
    return response;
  }

  // AI Report Methods
  async generateAIReport(searchId: number): Promise<{ success: boolean; message: string; ai_report: any }> {
    const response = await this.request<{ success: boolean; message: string; ai_report: any }>(
      `/search/${searchId}/ai-report/generate/`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  async getAIReport(searchId: number): Promise<any> {
    const response = await this.request<any>(`/search/${searchId}/ai-report/`);
    return response;
  }

  async refreshAIReport(searchId: number): Promise<{ success: boolean; message: string; ai_report: any }> {
    const response = await this.request<{ success: boolean; message: string; ai_report: any }>(
      `/search/${searchId}/ai-report/refresh/`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  async deactivateSearchRequest(searchId: number): Promise<any> {
    const response = await this.request<any>(`/search/${searchId}/deactivate/`, {
      method: 'POST',
    });
    return response;
  }

  // Credits Balance
  async getCreditsBalance(): Promise<{ balance: number }> {
    const response = await this.request<{ balance: number }>('/credits/balance/');
    return response;
  }

  // Transaction History
  async getTransactionHistory(params?: any): Promise<PaginatedResponse<any>> {
    const queryParams = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    const response = await this.request<PaginatedResponse<any>>(`/credits/transactions/${queryParams}`);
    return response;
  }

  // Subscription Management
  async getCurrentSubscription(): Promise<any> {
    const response = await this.request<any>('/subscriptions/current/');
    return response;
  }

  async purchaseSubscription(paymentData: any): Promise<any> {
    const response = await this.request<any>('/subscriptions/purchase/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
    return response;
  }

  async getSubscriptionHistory(params?: any): Promise<PaginatedResponse<any>> {
    const queryParams = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    const response = await this.request<PaginatedResponse<any>>(`/subscriptions/history/${queryParams}`);
    return response;
  }

  // Admin Methods
  async getAdminUsers(params?: any): Promise<PaginatedResponse<User>> {
    const queryParams = params ? `?${new URLSearchParams(params as any).toString()}` : '';
    const response = await this.request<PaginatedResponse<User>>(`/admin/users/${queryParams}`);
    return response;
  }

  async verifyUser(userId: number, verificationData: any): Promise<User> {
    const response = await this.request<User>(`/admin/users/${userId}/verify/`, {
      method: 'PUT',
      body: JSON.stringify(verificationData),
    });
    return response;
  }

  async blockUser(userId: number, blockData: any): Promise<User> {
    const response = await this.request<User>(`/admin/users/${userId}/block/`, {
      method: 'PUT',
      body: JSON.stringify(blockData),
    });
    return response;
  }

  async getAdminAPIs(): Promise<any[]> {
    const response = await this.request<PaginatedResponse<any>>('/admin/api-details/');
    return response.results || response;
  }

  async createAPI(apiData: any): Promise<any> {
    // Preserve original case for API data to maintain parameter names
    const response = await this.request<any>('/admin/api-details/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
    return response;
  }

  async updateAPI(apiId: number, apiData: any): Promise<any> {
    // Preserve original case for API data to maintain parameter names
    const response = await this.request<any>(`/admin/api-details/${apiId}/`, {
      method: 'PUT',
      body: JSON.stringify(apiData),
    });
    return response;
  }

  async deleteAPI(apiId: number): Promise<void> {
    await this.request<void>(`/admin/api-details/${apiId}/`, {
      method: 'DELETE',
    });
  }

  async testAPI(apiId: number, testData: any): Promise<any> {
    const response = await this.request<any>(`/admin/api-details/${apiId}/test/`, {
      method: 'POST',
      body: JSON.stringify(testData),
    });
    return response;
  }

  async updateAPIDisplayParameters(apiId: number, displayParameters: any): Promise<any> {
    const requestBody = { display_parameters: displayParameters };
    
    const response = await this.request<any>(`/admin/api-details/${apiId}/`, {
      method: 'PATCH',
      body: JSON.stringify(requestBody),
    });
    
    return response;
  }

  // System monitoring
  async getAPIHealth(): Promise<any[]> {
    const response = await this.request<any[]>('/admin/api/health/');
    return response;
  }

  // Report Templates
  async getReportTemplates(params?: {
    parameter_type?: number;
    is_active?: boolean;
    search?: string;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<ReportTemplate>> {
    const queryParams = new URLSearchParams();
    if (params?.parameter_type) queryParams.append('parameter_type', params.parameter_type.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.page_size) queryParams.append('page_size', params.page_size.toString());
    
    const queryString = queryParams.toString();
    const url = queryString ? `/admin/report-templates/?${queryString}` : '/admin/report-templates/';
    const response = await this.request<PaginatedResponse<ReportTemplate>>(url);
    return response;
  }

  async getReportTemplate(id: number): Promise<ReportTemplate> {
    const response = await this.request<ReportTemplate>(`/admin/report-templates/${id}/`);
    return response;
  }

  async createReportTemplate(data: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await this.request<ReportTemplate>('/admin/report-templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  }

  async updateReportTemplate(id: number, data: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await this.request<ReportTemplate>(`/admin/report-templates/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
    return response;
  }

  async deleteReportTemplate(id: number): Promise<void> {
    await this.request(`/admin/report-templates/${id}/`, {
      method: 'DELETE',
    });
  }

  async setDefaultTemplate(id: number): Promise<ReportTemplate> {
    const response = await this.request<ReportTemplate>(`/admin/report-templates/${id}/set-default/`, {
      method: 'POST',
    });
    return response;
  }

  async getTemplatesByParameterType(parameterTypeId: number): Promise<ReportTemplate[]> {
    const response = await this.request<ReportTemplate[]>(
      `/admin/report-templates/by-parameter-type/?parameter_type_id=${parameterTypeId}`
    );
    return response;
  }

  async getDefaultTemplateByParameterType(parameterTypeId: number): Promise<ReportTemplate | null> {
    try {
      const response = await this.request<ReportTemplate>(
        `/admin/report-templates/default-by-parameter-type/?parameter_type_id=${parameterTypeId}`
      );
      return response;
    } catch {
      return null;
    }
  }

  async generateTemplatesFromDisplayParameters(params?: {
    parameter_type_id?: number;
    group_by_api?: boolean;
    merge_strategy?: 'first' | 'all' | 'best';
    overwrite_existing?: boolean;
    use_api_responses_fallback?: boolean;
  }): Promise<{
    success: boolean;
    created_templates: Array<{
      id: number;
      name: string;
      parameter_type: string;
      sections_count: number;
      fields_count: number;
    }>;
    updated_templates: Array<{
      id: number;
      name: string;
      parameter_type: string;
      sections_count: number;
      fields_count: number;
    }>;
    errors: Array<{
      parameter_type: string;
      error: string;
    }>;
    summary: {
      created: number;
      updated: number;
      errors: number;
    };
  }> {
    const response = await this.request<{
      success: boolean;
      created_templates: Array<{
        id: number;
        name: string;
        parameter_type: string;
        sections_count: number;
        fields_count: number;
      }>;
      updated_templates: Array<{
        id: number;
        name: string;
        parameter_type: string;
        sections_count: number;
        fields_count: number;
      }>;
      errors: Array<{
        parameter_type: string;
        error: string;
      }>;
      summary: {
        created: number;
        updated: number;
        errors: number;
      };
    }>(
      '/admin/report-templates/generate-from-display-parameters/',
      {
        method: 'POST',
        body: JSON.stringify(params || {}),
      }
    );
    return response;
  }

  async previewTemplateGeneration(params?: {
    parameter_type_id?: number;
    group_by_api?: boolean;
    use_api_responses_fallback?: boolean;
  }): Promise<{
    preview: Array<{
      parameter_type_id: number;
      parameter_type_name: string;
      apis_count: number;
      total_fields: number;
      apis: Array<{
        api_id: number;
        api_name: string;
        fields_count: number;
        fields: Array<{ field_path: string; display_name: string }>;
        sample_response?: any;
      }>;
      group_by_api: boolean;
    }>;
    summary: {
      parameter_types: number;
      total_apis: number;
      total_fields: number;
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params?.parameter_type_id) {
      queryParams.append('parameter_type_id', params.parameter_type_id.toString());
    }
    if (params?.group_by_api !== undefined) {
      queryParams.append('group_by_api', params.group_by_api.toString());
    }
    if (params?.use_api_responses_fallback !== undefined) {
      queryParams.append('use_api_responses_fallback', params.use_api_responses_fallback.toString());
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/admin/report-templates/preview-generation/?${queryString}` : '/admin/report-templates/preview-generation/';
    const response = await this.request<{
      preview: Array<{
        parameter_type_id: number;
        parameter_type_name: string;
        apis_count: number;
        total_fields: number;
        apis: Array<{
          api_id: number;
          api_name: string;
          fields_count: number;
          fields: Array<{ field_path: string; display_name: string }>;
        }>;
        group_by_api: boolean;
      }>;
      summary: {
        parameter_types: number;
        total_apis: number;
        total_fields: number;
      };
    }>(url);
    return response;
  }

  async getAdminAPIHealth(): Promise<any[]> {
    const response = await this.request<any[]>('/admin/api/health/');
    return response;
  }

  async getAdminAPIResponses(params?: {
    page?: number;
    page_size?: number;
    api_id?: number;
    status?: string;
    failed_only?: boolean;
    search?: string;
    ordering?: string;
  }): Promise<PaginatedResponse<any>> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, value.toString());
        }
      });
    }
    
    const queryString = query.toString();
    const endpoint = queryString ? `/admin/api-responses/?${queryString}` : '/admin/api-responses/';
    const response = await this.request<any>(endpoint);
    
    // Handle both paginated and non-paginated responses
    if (response.results !== undefined) {
      // Paginated response
      return response as PaginatedResponse<any>;
    } else if (Array.isArray(response)) {
      // Non-paginated array response
      return {
        count: response.length,
        next: undefined,
        previous: undefined,
        results: response
      } as PaginatedResponse<any>;
    } else {
      // Fallback
      return {
        count: 0,
        next: undefined,
        previous: undefined,
        results: []
      } as PaginatedResponse<any>;
    }
  }

  async getAdminAPILogs(params?: {
    page?: number;
    page_size?: number;
    level?: string;
    logger?: string;
    search_request_id?: number;
    api_id?: number;
    search?: string;
    ordering?: string;
  }): Promise<{ count: number; next: boolean; previous: boolean; results: any[] }> {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          query.append(key, value.toString());
        }
      });
    }
    const queryString = query.toString();
    const endpoint = queryString ? `/admin/api-logs/?${queryString}` : '/admin/api-logs/';
    return this.request<any>(endpoint);
  }

  async getSystemStats(): Promise<any> {
    const response = await this.request<any>('/admin/system/stats/');
    return response;
  }

  // Admin Subscription Plans (backend: /subscriptions/admin/...)
  async getAdminSubscriptionPlans(): Promise<any[]> {
    const response = await this.request<any[]>('/subscriptions/admin/plans/');
    return response;
  }

  async createAdminSubscriptionPlan(planData: any): Promise<any> {
    const snakeCaseData = camelToSnakeCase(planData);
    console.log('API Service - Original data:', planData);
    console.log('API Service - Converted to snake_case:', snakeCaseData);
    const response = await this.request<any>('/subscriptions/admin/plans/create/', {
      method: 'POST',
      body: JSON.stringify(snakeCaseData),
    });
    return response;
  }

  async updateAdminSubscriptionPlan(planId: number, planData: any): Promise<any> {
    const snakeCaseData = camelToSnakeCase(planData);
    console.log('API Service - Update Original data:', planData);
    console.log('API Service - Update Converted to snake_case:', snakeCaseData);
    const response = await this.request<any>(`/subscriptions/admin/plans/${planId}/update/`, {
      method: 'PUT',
      body: JSON.stringify(snakeCaseData),
    });
    return response;
  }

  async deleteAdminSubscriptionPlan(planId: number): Promise<void> {
    await this.request(`/subscriptions/admin/plans/${planId}/delete/`, {
      method: 'DELETE',
    });
  }

  async getAdminUserSubscriptions(): Promise<any[]> {
    const response = await this.request<any[]>('/subscriptions/admin/user-subscriptions/');
    return response;
  }

  // Admin Payments
  async getAdminPayments(): Promise<any[]> {
    const response = await this.request<any[]>('/admin/payments/');
    return response;
  }

  async getAdminPaymentStats(days: number = 30): Promise<any> {
    const response = await this.request<any>(`/admin/payments/stats/?days=${days}`);
    return response;
  }

  // Refund functionality removed



  async createSubscription(subscriptionData: any): Promise<any> {
    const response = await this.request<any>('/subscriptions/create_subscription/', {
      method: 'POST',
      body: JSON.stringify(subscriptionData),
    });
    return response;
  }

  async cancelSubscription(subscriptionId: number): Promise<any> {
    const response = await this.request<any>(`/subscriptions/${subscriptionId}/cancel/`, {
      method: 'POST',
    });
    return response;
  }

  async checkFeatureAccess(feature?: string): Promise<any> {
    const url = feature ? `/subscriptions/check_feature_access/?feature=${feature}` : '/subscriptions/check_feature_access/';
    const response = await this.request<any>(url);
    return response;
  }

  // User Payments
  async getPayments(): Promise<any[]> {
    const response = await this.request<any[]>('/payments/');
    return response;
  }

  // Refund functionality removed

  async createPaymentOrder(orderData: any): Promise<any> {
    const response = await this.request<any>('/payments/create_order/', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
    return response;
  }

  async verifyPayment(paymentData: any): Promise<any> {
    const response = await this.request<any>('/payments/verify_payment/', {
      method: 'POST',
      body: JSON.stringify(paymentData),
    });
    return response;
  }

  // Refund functionality removed

  // Export functionality
  async exportSearchResults(searchId: number, format: 'pdf' | 'csv' = 'pdf'): Promise<any> {
    const response = await this.request<any>(`/exports/search/${searchId}/`, {
      method: 'POST',
      body: JSON.stringify({ format }),
    });
    return response;
  }

  // Quick Import functionality
  async testQuickImport(apiData: any): Promise<any> {
    return this.request<any>('/api-details/quick-test/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  async quickImport(apiData: any): Promise<any> {
    return this.request<any>('/api-details/import-api-quick/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  async getAPITemplates(): Promise<any[]> {
    return this.request<any[]>('/api-details/templates/');
  }

  async validateAPIConfig(apiData: any): Promise<any> {
    return this.request<any>('/api-details/validate-config/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }




  async downloadExport(exportId: string): Promise<Blob> {
    const url = `${this.baseURL}/exports/${exportId}/download/`;
    const config: RequestInit = {
      headers: {
        ...(this.token && { Authorization: `Token ${this.token}` }),
      },
    };

    try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
        throw new APIError('Export download failed', response.status);
    }

      return await response.blob();
    } catch (error) {
      if (error instanceof APIError) {
      throw error;
      }
      throw new NetworkError('Export download failed');
    }
  }

  // Token management
  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('authToken', token);
  }

  getToken(): string | null {
    return this.token;
  }



  getBaseURL(): string {
    return this.baseURL;
  }

  isAuthenticated(): boolean {
    return !!this.token;
  }

  async validateToken(): Promise<boolean> {
    try {
      // Refresh token from localStorage
      this.token = localStorage.getItem('authToken');
      
      if (!this.token) {
        console.log('No token found in localStorage');
        return false;
      }

      // Test the token by making a simple API call
      await this.getUserProfile();
      console.log('Token validation successful');
      return true;
    } catch (error) {
      console.error('Token validation failed:', error);
      if (error instanceof AuthenticationError) {
        this.logout();
      }
      return false;
    }
  }

  async getPaymentMethods(): Promise<any[]> {
    const response = await this.request<any>('/payments/payment-methods/');
    // Handle both paginated and non-paginated responses
    if (Array.isArray(response)) {
      return response;
    } else if (response && Array.isArray(response.results)) {
      return response.results;
    }
    // Return empty array if response is unexpected
    console.warn('Unexpected payment methods response format:', response);
    return [];
  }

  async createOfflinePaymentRequest(data: any): Promise<any> {
    // data should include: payment (with plan, amount, method), reference_number, bank_details, payment_instructions, payment_proof
    const formData = new FormData();
    Object.entries(data).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (key === 'payment_proof' && value instanceof File) {
          formData.append(key, value);
        } else if (typeof value === 'object' && !(value instanceof File)) {
          // Only append if object is not empty
          const str = JSON.stringify(value);
          if (str !== '{}') {
            formData.append(key, str);
          }
        } else {
          formData.append(key, String(value));
        }
      }
    });
    return this.request<any>('/payments/offline-payment-requests/', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set multipart/form-data
    });
  }

  // Universal OSINT methods
  async universalOSINTSearch(searchData: UniversalOSINTSearchData): Promise<UniversalOSINTResponse> {
    try {
      const response = await this.request<UniversalOSINTResponse>('/osint/universal/', {
        method: 'POST',
        body: JSON.stringify(searchData),
      });
      return response;
    } catch (error) {
      console.error('Universal OSINT search failed:', error);
      throw error;
    }
  }

  async getUniversalOSINTRequests(): Promise<UniversalOSINTRequestSummary[]> {
    try {
      const response = await this.request<UniversalOSINTRequestSummary[]>('/osint/universal-requests/');
      return response;
    } catch (error) {
      console.error('Failed to get Universal OSINT requests:', error);
      throw error;
    }
  }

  async getUniversalOSINTRequestDetails(requestId: number): Promise<UniversalOSINTRequest> {
    try {
      const response = await this.request<UniversalOSINTRequest>(`/osint/universal-requests/${requestId}/`);
      return response;
    } catch (error) {
      console.error('Failed to get Universal OSINT request details:', error);
      throw error;
    }
  }

  async getUniversalOSINTRequestResults(requestId: number): Promise<UniversalOSINTResults> {
    try {
      const response = await this.request<UniversalOSINTResults>(`/osint/universal-requests/${requestId}/results/`);
      return response;
    } catch (error) {
      console.error('Failed to get Universal OSINT request results:', error);
      throw error;
    }
  }

  // Admin Universal OSINT methods
  async getAdminUniversalOSINTRequests(params?: Record<string, string>): Promise<any> {
    try {
      const queryParams = params ? new URLSearchParams(params).toString() : '';
      const url = `${this.baseURL}/osint/admin/universal-osint/${queryParams ? `?${queryParams}` : ''}`;
      
      // Make direct request to bypass normalization for paginated responses
      const config: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...(this.token && { Authorization: `Token ${this.token}` }),
        },
      };

      const response = await fetch(url, config);
      
      if (!response.ok) {
        throw this.createAPIError(response, await response.json().catch(() => ({})));
      }

      const data = await response.json();
      return data; // Return full response with pagination metadata
    } catch (error) {
      console.error('Failed to get admin Universal OSINT requests:', error);
      throw error;
    }
  }

  async submitAdminUniversalOSINTRequest(searchData: { search_value: string; request_data?: any }): Promise<any> {
    try {
      const response = await this.request<any>('/osint/admin/universal-osint/', {
        method: 'POST',
        body: JSON.stringify(searchData),
      });
      return response;
    } catch (error) {
      console.error('Failed to submit admin Universal OSINT request:', error);
      throw error;
    }
  }

  async getAdminUniversalOSINTRequestDetails(requestId: number): Promise<any> {
    try {
      const response = await this.request<any>(`/osint/admin/universal-osint/${requestId}/`);
      return response;
    } catch (error) {
      console.error('Failed to get admin Universal OSINT request details:', error);
      throw error;
    }
  }

  // Account Finder Methods
  async findAccounts(searchData: { parameterType: number; searchValue: string }): Promise<{ request_id: number; status: string; message: string }> {
    const response = await this.request<{ request_id: number; status: string; message: string }>('/account-finder/find-accounts/', {
      method: 'POST',
      body: JSON.stringify({
        parameter_type: searchData.parameterType,
        search_value: searchData.searchValue
      }),
    });
    return response;
  }

  async getAccountCheckResults(requestId: number): Promise<any> {
    return this.request<any>(`/account-finder/results/${requestId}/`);
  }

  async getSupportedPlatforms(parameterTypeId: number): Promise<any[]> {
    return this.request<any[]>(`/account-finder/supported-platforms/?parameter_type_id=${parameterTypeId}`);
  }

  async getAccountCheckHistory(): Promise<any[]> {
    return this.request<any[]>('/account-finder/requests/');
  }

  async getAccountCheckStatistics(): Promise<any> {
    return this.request<any>('/account-finder/requests/statistics/');
  }

  // Session Management Methods
  async uploadSession(sessionData: {
    platform: string;
    session_data: any;
    expires_hours: number;
  }): Promise<any> {
    return this.request<any>('/account-finder/sessions/upload/', {
      method: 'POST',
      body: JSON.stringify(sessionData),
    });
  }

  async getUserSessions(): Promise<any[]> {
    return this.request<any[]>('/account-finder/sessions/');
  }

  async getSessionStatus(): Promise<any> {
    return this.request<any>('/account-finder/sessions/status/');
  }

  async deleteSession(platform: string): Promise<any> {
    return this.request<any>(`/account-finder/sessions/${platform}/delete/`, {
      method: 'DELETE',
    });
  }

  async verifySession(platform: string): Promise<{ valid: boolean; error?: string; message?: string }> {
    return this.request<{ valid: boolean; error?: string; message?: string }>(`/account-finder/sessions/${platform}/verify/`, {
      method: 'POST'
    });
  }

  // Law Enforcement Profile Methods
  async createLawEnforcementProfile(): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>('/law-enforcement/profile/create/', {
      method: 'POST'
    });
  }

  async getLawEnforcementProfile(): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>('/law-enforcement/profile/');
  }

  async updateLawEnforcementProfile(profileData: LawEnforcementProfileFormData): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>('/law-enforcement/profile/update/', {
      method: 'PUT',
      body: JSON.stringify(camelToSnakeCase(profileData))
    });
  }

  async sendGovernmentEmailOTP(emailData: GovernmentEmailOTPData): Promise<{ message: string; email: string }> {
    return this.request<{ message: string; email: string }>('/law-enforcement/email/send-otp/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(emailData))
    });
  }

  async verifyGovernmentEmailOTP(otpData: GovernmentEmailVerifyData): Promise<{ message: string; verified: boolean }> {
    return this.request<{ message: string; verified: boolean }>('/law-enforcement/email/verify-otp/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(otpData))
    });
  }

  async uploadGovernmentId(file: FormData): Promise<{ message: string; file_url: string }> {
    return this.request<{ message: string; file_url: string }>('/law-enforcement/upload-id/', {
      method: 'POST',
      body: file,
      headers: {} // Let the browser set Content-Type for FormData
    });
  }

  async submitLawEnforcementVerification(verificationData: {
    departmentName: string;
    rankPosition: string;
    officeAddress: string;
    whatsappNumber: string;
    telegramNumber: string;
    governmentEmail: string;
    governmentEmailVerified: boolean;
    governmentIdFile?: string;
  }): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>('/law-enforcement/submit-verification/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(verificationData))
    });
  }

  async getCurrentOTP(): Promise<{
    otp: string;
    email: string;
    expires_at: string;
    verified: boolean;
  }> {
    return this.request('/law-enforcement/debug/otp/');
  }

  // Admin Law Enforcement Methods
  async getLawEnforcementProfiles(): Promise<LawEnforcementProfile[]> {
    return this.request<LawEnforcementProfile[]>('/admin/law-enforcement/profiles/');
  }

  async approveLawEnforcementProfile(profileId: number): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>(`/admin/law-enforcement/profiles/${profileId}/approve/`, {
      method: 'POST'
    });
  }

  async rejectLawEnforcementProfile(profileId: number, rejectionData: {
    rejectionReason: string;
    adminNotes: string;
  }): Promise<LawEnforcementProfile> {
    return this.request<LawEnforcementProfile>(`/admin/law-enforcement/profiles/${profileId}/reject/`, {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(rejectionData))
    });
  }
}

export const apiService = new CentralizedAPIService();

// AI Providers Service
export const aiProvidersService = {
  async listProviders(): Promise<AIProvider[]> {
    const response = await apiService.adminRequest<any>('/ai-services/providers/');
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results;
    }
    return Array.isArray(response) ? response : [];
  },

  async getProvider(id: number): Promise<AIProvider> {
    const response = await apiService.adminRequest<AIProvider>(`/ai-services/providers/${id}/`);
    return response;
  },

  async createProvider(data: Partial<AIProviderFormData>): Promise<AIProvider> {
    const response = await apiService.adminRequest<AIProvider>('/ai-services/providers/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  async updateProvider(id: number, data: Partial<AIProviderFormData>): Promise<AIProvider> {
    const response = await apiService.adminRequest<AIProvider>(`/ai-services/providers/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  async deleteProvider(id: number): Promise<void> {
    await apiService.adminRequest(`/ai-services/providers/${id}/`, {
      method: 'DELETE',
    });
  },

  async activateProvider(id: number): Promise<any> {
    const response = await apiService.adminRequest(`/ai-services/providers/${id}/activate/`, {
      method: 'POST',
    });
    return response;
  },

  async deactivateProvider(id: number): Promise<any> {
    const response = await apiService.adminRequest(`/ai-services/providers/${id}/deactivate/`, {
      method: 'POST',
    });
    return response;
  },

  async getAvailableProviders(): Promise<AIProvider[]> {
    const response = await apiService.request<{ success: boolean; providers: AIProvider[] }>('/ai-services/providers/available/');
    return response.providers || [];
  },
};

// AI Summary Config Service
export const aiSummaryConfigService = {
  async listConfigs(): Promise<AISummaryConfig[]> {
    const response = await apiService.adminRequest<any>('/admin/ai-summary-configs/');
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results;
    }
    return Array.isArray(response) ? response : [];
  },

  async getConfig(id: number): Promise<AISummaryConfig> {
    const response = await apiService.adminRequest<AISummaryConfig>(`/admin/ai-summary-configs/${id}/`);
    return response;
  },

  async getCurrentDefault(): Promise<{ exists: boolean; config?: AISummaryConfig; message?: string }> {
    const response = await apiService.adminRequest<any>('/admin/ai-summary-configs/current_default/');
    return response;
  },

  async createConfig(data: Partial<any>): Promise<AISummaryConfig> {
    const response = await apiService.adminRequest<AISummaryConfig>('/admin/ai-summary-configs/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  async updateConfig(id: number, data: Partial<any>): Promise<AISummaryConfig> {
    const response = await apiService.adminRequest<AISummaryConfig>(`/admin/ai-summary-configs/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  async deleteConfig(id: number): Promise<void> {
    await apiService.adminRequest(`/admin/ai-summary-configs/${id}/`, {
      method: 'DELETE',
    });
  },

  async setAsDefault(id: number): Promise<any> {
    const response = await apiService.adminRequest(`/admin/ai-summary-configs/${id}/set_as_default/`, {
      method: 'POST',
    });
    return response;
  },
};

// Expose API service globally for debugging (development only)
if (process.env.NODE_ENV === 'development') {
  (window as any).apiService = apiService;
}

// API Management (Admin)


export const apiAdmin = {
  async listApis(params?: {
    parameter_type_id?: number;
    status?: string;
  }): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params?.parameter_type_id) {
      queryParams.append('parameter_type_id', params.parameter_type_id.toString());
    }
    if (params?.status) {
      queryParams.append('status', params.status);
    }
    const queryString = queryParams.toString();
    const url = queryString ? `/admin/api-details/?${queryString}` : '/admin/api-details/';
    const response = await apiService.adminRequest<any>(url);
    // Handle both paginated and direct array responses
    if (response && typeof response === 'object' && 'results' in response) {
      return response.results;
    }
    return Array.isArray(response) ? response : [];
  },

  async getApi(id: number): Promise<any> {
    const response = await apiService.adminRequest<any>(`/admin/api-details/${id}/`);
    return response;
  },

  async createApi(data: Partial<any>): Promise<any> {
    // Preserve original case for API data to maintain parameter names
    const response = await apiService.adminRequest<any>('/admin/api-details/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  async updateApi(id: number, data: Partial<any>): Promise<any> {
    // Preserve original case for API data to maintain parameter names
    const response = await apiService.adminRequest<any>(`/admin/api-details/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  async deleteApi(id: number): Promise<void> {
    await apiService.adminRequest(`/admin/api-details/${id}/`, {
      method: 'DELETE',
    });
  },

  async activateApi(id: number): Promise<any> {
    const response = await apiService.adminRequest(`/admin/api-details/${id}/activate/`, {
      method: 'POST',
    });
    return response;
  },

  async deactivateApi(id: number): Promise<any> {
    const response = await apiService.adminRequest(`/admin/api-details/${id}/deactivate/`, {
      method: 'POST',
    });
    return response;
  },

  async bulkActivateApis(): Promise<any> {
    const response = await apiService.adminRequest(`/admin/api-details/bulk_activate/`, {
      method: 'POST',
    });
    return response;
  },

  async bulkDeactivateApis(): Promise<any> {
    const response = await apiService.adminRequest(`/admin/api-details/bulk_deactivate/`, {
      method: 'POST',
    });
    return response;
  },

  async testApi(id: number, testValue: string, useTestMode = true, countryCode?: string): Promise<any> {
    const requestData: any = { test_value: testValue, use_test_mode: useTestMode };
    if (countryCode) {
      requestData.country_code = countryCode;
    }
    const response = await apiService.adminRequest(`/admin/api-details/${id}/test/`, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
    return response;
  },

  async previewApiRequest(id: number, testValue: string, countryCode?: string): Promise<{ request: any; error?: string }> {
    const requestData: any = { test_value: testValue, use_test_mode: true };
    if (countryCode) {
      requestData.country_code = countryCode;
    }
    return apiService.adminRequest(`/admin/api-details/${id}/preview/`, {
      method: 'POST',
      body: JSON.stringify(requestData),
    });
  },

  async listFieldMappings(apiId: number): Promise<any[]> {
    const response = await apiService.adminRequest<any[]>(`/admin/api-details/field-mappings/?api=${apiId}`);
    return response;
  },

  async createFieldMapping(data: Partial<any>): Promise<any> {
    const response = await apiService.adminRequest<any>('/admin/api-details/field-mappings/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response;
  },

  async updateFieldMapping(id: number, data: Partial<any>): Promise<any> {
    const response = await apiService.adminRequest<any>(`/admin/api-details/field-mappings/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return response;
  },

  async deleteFieldMapping(id: number): Promise<void> {
    await apiService.adminRequest(`/admin/api-details/field-mappings/${id}/`, {
      method: 'DELETE',
    });
  },

  // API Transformation Utility Methods
  async transformAPIs(): Promise<any> {
    return apiService.adminRequest('/admin/api-transformation/', { method: 'POST' });
  },

  async validateAPIs(): Promise<any> {
    return apiService.adminRequest('/admin/api-transformation/validate/', { method: 'POST' });
  },

  async exportAPIs(): Promise<any> {
    return apiService.adminRequest('/admin/api-transformation/export/', { method: 'GET' });
  },

  async importAPIs(formData: FormData): Promise<any> {
    return apiService.adminRequest('/admin/api-transformation/import_apis/', { 
      method: 'POST',
      body: formData,
      headers: {
        // Don't set Content-Type for FormData, let browser set it
      }
    });
  },

  async bulkUpdateAPIs(updates: any): Promise<any> {
    return apiService.adminRequest('/admin/api-transformation/bulk_update/', { 
      method: 'POST',
      body: JSON.stringify({ updates })
    });
  },

  // RapidAPI Import
  async importRapidAPI(content: string, parameterTypeId: number): Promise<any> {
    return apiService.adminRequest('/admin/api-details/import_rapidapi/', {
      method: 'POST',
      body: JSON.stringify({
        content,
        parameter_type_id: parameterTypeId
      }),
    });
  },

  // Execute exact request (used by Basic & Config Test API from preview)
  async executeRawRequest(payload: { method: string; url: string; headers: Record<string, string>; body?: string | object | null }): Promise<any> {
    return apiService.adminRequest('/admin/api-details/execute_raw_request/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Quick Import methods
  async testQuickImport(apiData: any): Promise<any> {
    return apiService.adminRequest('/admin/api-details/quick-test/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  },

  async quickImport(apiData: any): Promise<any> {
    return apiService.adminRequest('/admin/api-details/import_api_quick/', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  },

  async getIntegrationPatterns(): Promise<any> {
    return apiService.adminRequest('/admin/api-details/integration_patterns/');
  },

  async previewCurl(content: string, parameterTypeId: number): Promise<any> {
    return apiService.adminRequest('/admin/api-details/preview_curl/', {
      method: 'POST',
      body: JSON.stringify({ content, parameter_type_id: parameterTypeId }),
    });
  },

  async importCurl(content: string, parameterTypeId: number): Promise<any> {
    return apiService.adminRequest('/admin/api-details/import_curl/', {
      method: 'POST',
      body: JSON.stringify({ content, parameter_type_id: parameterTypeId }),
    });
  },

  async autoMapFields(
    apiId: number,
    options?: { testValue?: string; countryCode?: string; responseData?: unknown; save?: boolean }
  ): Promise<any> {
    return apiService.adminRequest(`/admin/api-details/${apiId}/auto_map_fields/`, {
      method: 'POST',
      body: JSON.stringify({
        test_value: options?.testValue,
        country_code: options?.countryCode,
        response_data: options?.responseData,
        save: options?.save ?? true,
      }),
    });
  },

  async getOrganizations(): Promise<any[]> {
    return apiService.adminRequest('/admin/organizations/');
  },

  async getSearchParameters(): Promise<any[]> {
    return apiService.adminRequest('/admin/search-parameters/');
  },

};

// API Key Management Methods
export const apiKeyService = {
  async getAPIKeys(): Promise<PaginatedResponse<APIKey>> {
    return apiService.request('/api-keys/keys/');
  },

  async createAPIKey(apiKeyData: APIKeyCreate): Promise<APIKey & { api_key: string; secret_key: string; auth_token: string }> {
    return apiService.request('/api-keys/keys/', {
      method: 'POST',
      body: JSON.stringify(camelToSnakeCase(apiKeyData))
    });
  },

  async updateAPIKey(apiKeyId: string, apiKeyData: APIKeyUpdate): Promise<APIKey> {
    return apiService.request(`/api-keys/keys/${apiKeyId}/`, {
      method: 'PATCH',
      body: JSON.stringify(camelToSnakeCase(apiKeyData))
    });
  },

  async deleteAPIKey(apiKeyId: string): Promise<void> {
    return apiService.request(`/api-keys/keys/${apiKeyId}/`, {
      method: 'DELETE'
    });
  },

  async revokeAPIKey(apiKeyId: string): Promise<{ message: string }> {
    return apiService.request(`/api-keys/keys/${apiKeyId}/revoke/`, {
      method: 'POST'
    });
  },

  async regenerateAPIKey(apiKeyId: string): Promise<{
    message: string;
    new_api_key: {
      id: string;
      name: string;
      api_key: string;
      secret_key: string;
      auth_token: string;
    };
  }> {
    return apiService.request(`/api-keys/keys/${apiKeyId}/regenerate/`, {
      method: 'POST'
    });
  },

  async getAPIKeyUsage(apiKeyId: string): Promise<PaginatedResponse<APIKeyUsage>> {
    return apiService.request(`/api-keys/keys/${apiKeyId}/usage/`);
  },

  async getAPIKeyStats(): Promise<APIKeyStats> {
    return apiService.request('/api-keys/keys/stats/');
  },

  async testAPIKeyAccess(testData: APIKeyTest): Promise<APIKeyTestResult> {
    console.log('testAPIKeyAccess input:', testData);
    const convertedData = camelToSnakeCase(testData);
    console.log('testAPIKeyAccess converted:', convertedData);
    return apiService.request('/api-keys/keys/test_access/', {
      method: 'POST',
      body: JSON.stringify(convertedData)
    });
  },

  async getAPIKeyDocumentation(): Promise<APIKeyDocumentation> {
    return apiService.request('/api-keys/documentation/');
  }
};
